// P1-T14: classify a single SQL statement for the subset-gated pgls PGQ
// suppressor rule (R4 in `suppressor.ts`).
//
// The PG language server (pgls) predates PG19 SQL/PGQ and raises FALSE syntax
// diagnostics on valid PGQ. We only want to suppress those on statements that
// (a) genuinely contain a PGQ construct AND (b) stay inside the subset of
// SQL/PGQ that both real PG19 and our tree-sitter fork verifiably accept.
// Anything ambiguous keeps the marker — a false squiggle beats a hidden error.
//
// This is a pure text/token scan with NO tree-sitter dependency at this
// boundary (mirroring the R3 refactor). The suppressor's own clean-parse gate
// (`!insideError`) is what proves our grammar accepted the statement; this
// function only decides PGQ-ness and subset membership.

import { maskStringsAndComments } from '@/features/workspace/common/utils/sqlTextMasker';

/** GRAPH_TABLE table function — the row-source form of SQL/PGQ. */
const GRAPH_TABLE_RE = /\bGRAPH_TABLE\s*\(/i;
/** CREATE/ALTER/DROP [TEMP[ORARY]] PROPERTY GRAPH — the DDL form. */
const PROPERTY_GRAPH_DDL_RE =
  /\b(CREATE|ALTER|DROP)\s+(TEMP(ORARY)?\s+)?PROPERTY\s+GRAPH\b/i;
const MATCH_RE = /\bMATCH\b/i;
const COLUMNS_RE = /\bCOLUMNS\b/i;

// Constructs outside the PG19-verified subset, scoped to the MATCH pattern.
/** Quantifier braces: `{1}`, `{1,3}`, `{1,}` after a node/edge pattern. */
const QUANTIFIER_BRACE_RE = /\{\s*\d/;
/** Bare quantifier symbols in pattern position: `*`, `+`, `?`. */
const BARE_QUANTIFIER_RE = /[*+?]/;
/** Path modes between MATCH and the first `(`. */
const PATH_MODE_RE = /\b(WALK|TRAIL|ACYCLIC|SIMPLE)\b/i;
/** Search prefixes between MATCH and the first `(`. */
const PREFIX_RE = /\b(ANY|ALL\s+SHORTEST|SHORTEST)\b/i;

export type PgqSuppressionClass =
  | 'pgq-supported-subset'
  | 'pgq-outside-subset'
  | 'not-pgq';

/**
 * Classify the statement covered by `stmtRange` for R4 suppression.
 *
 *  - `'not-pgq'`             — no PGQ construct; R4 never applies.
 *  - `'pgq-outside-subset'`  — PGQ, but uses a construct we don't verify on
 *                              PG19 (quantifiers, path modes, search prefixes)
 *                              or a malformed GRAPH_TABLE; keep the marker.
 *  - `'pgq-supported-subset'`— PGQ within the verified subset; R4 may suppress
 *                              (subject to the suppressor's other gates).
 *
 * Conservative by construction: when the shape is ambiguous we return
 * `'pgq-outside-subset'` so the (possibly-false) pgls squiggle survives rather
 * than risk hiding a real error.
 */
export function classifyPgqStatementForSuppression(
  source: string,
  stmtRange: { start: number; end: number },
): PgqSuppressionClass {
  const start = Math.max(0, Math.min(stmtRange.start, source.length));
  const end = Math.max(start, Math.min(stmtRange.end, source.length));
  // Mask strings/comments so a literal like `'GRAPH_TABLE('` or a commented-out
  // graph query can't trigger a false PGQ classification. `masked` keeps the
  // same character offsets as the slice (masked spans become spaces).
  // SQL/PGQ is PostgreSQL-only, so the masker gets `pgsql`: `"…"` is an
  // identifier, while `[` is an array subscript/constructor — NOT an
  // identifier delimiter — so `ARRAY['GRAPH_TABLE(']` is masked as the string
  // it is, as is a dollar-quoted `$$ … GRAPH_TABLE( … $$` routine body (pgls
  // does not parse PGQ inside one either, so classifying it `'not-pgq'` keeps
  // the marker — the conservative direction this module is built on).
  const masked = maskStringsAndComments(source.slice(start, end), { engine: 'pgsql' });

  const graphTableMatch = GRAPH_TABLE_RE.exec(masked);
  const isPropertyGraphDdl = PROPERTY_GRAPH_DDL_RE.test(masked);

  if (!graphTableMatch && !isPropertyGraphDdl) return 'not-pgq';

  // Property-graph DDL (CREATE/ALTER/DROP PROPERTY GRAPH) carries no MATCH
  // pattern, so there's nothing that can fall outside the subset. Malformed
  // DDL is caught upstream by the suppressor's clean-parse gate (`!insideError`).
  if (!graphTableMatch) return 'pgq-supported-subset';

  // GRAPH_TABLE(...) — inspect the parenthesized body's MATCH pattern.
  const openIdx = masked.indexOf('(', graphTableMatch.index);
  if (openIdx < 0) return 'pgq-outside-subset';
  const closeIdx = findMatchingParen(masked, openIdx);
  const bodyEnd = closeIdx < 0 ? masked.length : closeIdx;
  const body = masked.slice(openIdx + 1, bodyEnd);

  const matchM = MATCH_RE.exec(body);
  // A real GRAPH_TABLE always has a MATCH clause. Its absence means this is a
  // malformed graph query or a same-named function call — keep the marker.
  if (!matchM) return 'pgq-outside-subset';
  const afterMatch = matchM.index + matchM[0].length;

  // Pattern window: everything between MATCH and COLUMNS (or the body end).
  // The COLUMNS projection list is deliberately excluded — it can hold
  // arithmetic (`a.x * 2`) that must not read as a graph quantifier.
  const colsM = COLUMNS_RE.exec(body.slice(afterMatch));
  const patternEnd = colsM ? afterMatch + colsM.index : body.length;
  const patternWindow = body.slice(afterMatch, patternEnd);

  // Quantifiers anywhere in the pattern window → outside subset.
  if (QUANTIFIER_BRACE_RE.test(patternWindow)) return 'pgq-outside-subset';
  if (BARE_QUANTIFIER_RE.test(patternWindow)) return 'pgq-outside-subset';

  // Path modes / search prefixes live between MATCH and the first `(`.
  const firstParen = patternWindow.indexOf('(');
  const prefixWindow =
    firstParen < 0 ? patternWindow : patternWindow.slice(0, firstParen);
  if (PATH_MODE_RE.test(prefixWindow)) return 'pgq-outside-subset';
  if (PREFIX_RE.test(prefixWindow)) return 'pgq-outside-subset';

  return 'pgq-supported-subset';
}

/**
 * Index of the `)` matching the `(` at `openIdx` in `s` (which is already
 * string/comment-masked, so every paren here is real code). Returns -1 if
 * unbalanced.
 */
function findMatchingParen(s: string, openIdx: number): number {
  let depth = 0;
  for (let i = openIdx; i < s.length; i += 1) {
    const c = s[i];
    if (c === '(') {
      depth += 1;
    } else if (c === ')') {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}
